'use client'
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/20/solid'
import { Button } from '@/components/Button'
import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { useState, useCallback, useMemo, useEffect, useContext } from 'react'
import { ProductListItemFilter } from './Filter'
import { Filter, FilterChangeFn } from './types'
import { Table } from './Table'
import Loader from '../Loader'
import { useTranslation } from 'react-i18next'
import {
    ProductGroup,
    ProductSort_Field,
    ProductsConnectionEdge,
    SortOrder,
} from '@/eshop/gql/graphql'
import { getProductsForGroup } from '@/eshop/queries'
import basketProduct from '../../../public/basket-product.png'
import Image from 'next/image'
import { useK2Setup } from '@/eshop/hooks/useK2Setup'
import { hydrateResult, removeTokenFromCookie } from '@/eshop/utils/client'
import { defaultManufacturer } from '@/eshop/constants'

interface Props {
    item: ProductGroup
    isOpen: boolean
    onClick: () => void
    groupProducts?: ProductsConnectionEdge[]
    selectedCategoryId: string | null
}

export function ProdutListItem({
    item,
    onClick,
    isOpen,
    groupProducts,
    selectedCategoryId,
}: Props) {
    const [filter, setFilter] = useState<Filter>({
        manufacturer: !!selectedCategoryId ? null : defaultManufacturer,
    })
    const [showAll, setShowAll] = useState(false)
    const { t } = useTranslation()
    const k2 = useK2Setup()

    useEffect(() => {
        setFilter((state) => ({
            ...state,
            manufacturer: !!selectedCategoryId ? null : defaultManufacturer,
        }))
    }, [selectedCategoryId])

    // Fetches basic variants info from API
    const { data: variantsResult, isFetched } = useQuery({
        queryKey: [
            'product',
            item.id,
            'variants',
            k2.currency,
            k2.languageId,
            selectedCategoryId,
        ],
        queryFn: () =>
            getProductsForGroup({
                after: '',
                before: '',
                first: 1000,
                last: 10000,
                filter: [
                    {
                        productPrices_currency: {
                            eq: k2.currency,
                        },
                        productGroup_id: { eq: item.id },
                        categories_id: {
                            eq: selectedCategoryId,
                        },
                    },
                ],
                sort: [
                    {
                        field: ProductSort_Field.ProductLangItemsCfQuality,
                        order: SortOrder.Asc,
                    },
                    {
                        field: ProductSort_Field.Abbr,
                        order: SortOrder.Asc,
                    },
                ],
            }).toPromise(),
        enabled: isOpen,
    })

    useEffect(() => {
        hydrateResult(variantsResult)?.match?.({
            Ok: () => {},
            Error: (e) => {
                if (e._tag === 'HydrateError') {
                    return
                }
                const code = e.error?.response?.code as number | undefined
                const msg = e.error?.response?.message as string | undefined
                if (code && msg?.includes('JWT')) {
                    removeTokenFromCookie()
                    window.location.reload()
                }
            },
        })
    }, [variantsResult])

    const variants = useMemo(
        () =>
            hydrateResult(variantsResult)?.match?.({
                Ok: (v) => v.products.edges || [],
                Error: (e) => [],
            }) ?? [],
        [variantsResult]
    )

    const handleUpdateFilter = useCallback<FilterChangeFn>(
        (key, value) => () => {
            setFilter((state) => ({
                ...state,
                [key]: value,
            }))
        },
        []
    )

    // reset filter after item is opened
    const handleItemClick = useCallback(() => {
        setShowAll(false)
        setFilter({
            manufacturer: selectedCategoryId ? null : defaultManufacturer,
        })
        onClick()
    }, [onClick, selectedCategoryId])

    // prevent event, otherwise whole product is closed
    const handleItemWrapperClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            e.preventDefault()
            e.stopPropagation()
        },
        []
    )

    const allFilteredVariants = useMemo(() => {
        if (!filter.manufacturer) {
            return groupProducts || variants || []
        }
        return (groupProducts || variants || []).filter((variant) => {
            let pl = variant.node.productLangItems.find(
                (pl) => pl.languageId === k2.languageId
            )
            if (
                filter.manufacturer === t('eshop.productlist.other') &&
                pl?.brandName === null
            ) {
                return pl
            }
            if (!pl && variant.node.productLangItems.length === 1) {
                pl = variant.node.productLangItems[0]
            }
            return pl ? pl.brandName === filter.manufacturer : false
        })
    }, [filter.manufacturer, groupProducts, variants, k2.languageId])

    // show only first 10 if user didnt click on Show all btn
    const filteredVariants = useMemo(
        () => allFilteredVariants.slice(0, 10),
        [allFilteredVariants]
    )

    // #LACS-212 if no variants are found and default manufacturer is selected, reset filter to show all variants
    useEffect(() => {
        if (
            filter.manufacturer === defaultManufacturer &&
            allFilteredVariants.length === 0 &&
            variants.length > 0
        ) {
            setFilter((state) => ({
                ...state,
                manufacturer: null,
            }))
        }
    }, [allFilteredVariants, filter.manufacturer, variants])

    return (
        <>
            <ProductListItemRow
                name={item.sortingNameLang}
                onClick={handleItemClick}
                rightAdornment={
                    <div className="text-sm flex items-center flex-shrink-0 w-1/4 md:w-auto">
                        {t('eshop.productlist.pickVariant')}
                        {isOpen ? (
                            <ChevronUpIcon
                                className="flex-shrink-0"
                                width={20}
                                height={20}
                            />
                        ) : (
                            <ChevronDownIcon
                                className="flex-shrink-0"
                                width={20}
                                height={20}
                            />
                        )}
                    </div>
                }
            />
            <div
                className={clsx(
                    'transition-all duration-200 overflow-x-scroll md:overflow-visible',
                    isOpen ? 'max-h-auto' : 'max-h-0'
                )}
                onClick={handleItemWrapperClick}
            >
                {isOpen && (
                    <Loader isLoading={!isFetched}>
                        <>
                            <ProductListItemFilter
                                variants={variants || []}
                                filter={filter}
                                onFilterChange={handleUpdateFilter}
                            />
                            <Table
                                brand={filter.manufacturer}
                                productGroupName={item.sortingNameLang}
                                variants={
                                    showAll
                                        ? allFilteredVariants
                                        : filteredVariants
                                }
                                className="my-4"
                            />
                            {allFilteredVariants.length >
                                filteredVariants.length &&
                                !showAll && (
                                    <div className="flex justify-center mb-4">
                                        <Button
                                            onClick={() => setShowAll(true)}
                                            color="secondary"
                                            size="sm"
                                        >
                                            {t('eshop.productlist.showAllBtn')}
                                        </Button>
                                    </div>
                                )}
                        </>
                    </Loader>
                )}
            </div>
        </>
    )
}

interface RowProps {
    name: string
    onClick?: () => void
    rightAdornment?: React.ReactNode | null
}

export function ProductListItemRow({
    name,
    onClick,
    rightAdornment,
}: RowProps) {
    return (
        <div
            className={clsx(
                'bg-gray-light px-6 py-2 mb-2 rounded-lg flex justify-between items-center gap-4',
                !!onClick && 'cursor-pointer'
            )}
            onClick={onClick}
        >
            <div className="flex items-center gap-4">
                <Image
                    src={basketProduct}
                    width={50}
                    height={50}
                    alt={name}
                    className="hidden sm:block"
                />
                <span className="text-lg">{name}</span>
            </div>
            {rightAdornment}
        </div>
    )
}
